/**
 * B-esper Bot Widget Component
 * Easy integration component for the chat widget
 */

import React, { useEffect, useRef, useState } from 'react';
import sdkLoader from '../utils/sdkLoader';
import './BesperBotWidget.css';

const BesperBotWidget = ({ 
  autoInitialize = true,
  widget = true,
  containerId,
  customBotId,
  customApiEndpoint,
  resourceId,
  className = '',
  style = {}
}) => {
  const containerRef = useRef(null);
  const botInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [isLocalhost, setIsLocalhost] = useState(false);

  useEffect(() => {
    if (!autoInitialize) {
      return;
    }

    const initializeBot = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Detect localhost
        const hostname = window.location.hostname;
        const isLocal = hostname === 'localhost' || 
                       hostname === '127.0.0.1' || 
                       hostname.startsWith('192.168.') ||
                       hostname.startsWith('10.') ||
                       hostname.includes('local');
        setIsLocalhost(isLocal);

        const botId = resourceId || customBotId || sdkLoader.getConfig().botId || 'public_bot_external';
        
        // For widget mode, use sdkLoader
        if (widget) {
          const instance = await sdkLoader.initBot({
            botId: botId,
            widget: true
          });
          botInstanceRef.current = instance;
          setIsInitialized(true);
          setIsLoading(false);
          return;
        }

        // For container mode, wait for container to exist
        if (!containerRef.current) {
          throw new Error('Container element not found');
        }

        // Load the SDK script which serves inline code
        await sdkLoader.loadWrapper();
        
        // The SDK script creates window.BesperBot[botId]
        // For container mode, we need to initialize it in the container
        if (window.BesperBot && window.BesperBot[botId]) {
          // Initialize container mode
          const botApi = window.BesperBot[botId];
          if (botApi.initContainer && containerId) {
            botApi.initContainer(containerId);
          }
          botInstanceRef.current = {
            destroy: () => {
              if (botApi.destroy) {
                botApi.destroy();
              }
            },
            show: () => {
              if (botApi.show) {
                botApi.show();
              }
            },
            hide: () => {
              if (botApi.hide) {
                botApi.hide();
              }
            }
          };
          setIsInitialized(true);
          setIsLoading(false);
          console.log('✅ Besper Bot Widget initialized successfully in container mode');
        } else {
          throw new Error('Bot API not available after loading SDK');
        }
      } catch (err) {
        console.error('❌ Failed to initialize Besper Bot Widget:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    // Small delay to ensure container is mounted
    const timer = setTimeout(() => {
      initializeBot();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (botInstanceRef.current) {
        try {
          botInstanceRef.current.destroy();
        } catch (e) {
          console.error('Error destroying bot instance:', e);
        }
        botInstanceRef.current = null;
        setIsInitialized(false);
      }
    };
  }, [autoInitialize, widget, containerId, customBotId, customApiEndpoint, resourceId]);

  // Don't render anything for widget mode - the SDK handles the DOM
  if (widget) {
    return null;
  }

  // For container mode, render the container
  return (
    <div 
      ref={containerRef}
      id={containerId}
      className={`besper-bot-container ${className}`}
      style={style}
    >
      {error && (
        <div className="besper-error-message">
          <p>Failed to load chat widget: {error}</p>
          {isLocalhost && (
            <p className="besper-debug-info">
              Debug: Make sure the API is running on http://localhost:5100
            </p>
          )}
        </div>
      )}
      
      {isLoading && (
        <div className="besper-loading-message">
          <div className="besper-loading-spinner"></div>
          <p>Loading chat widget...</p>
        </div>
      )}
      
      {!isLoading && !error && !isInitialized && (
        <div className="besper-placeholder">
          <p>Chat widget will appear here</p>
        </div>
      )}
    </div>
  );
};

export default BesperBotWidget;
